import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { BiArrowFromLeft, BiTask, BiCodeAlt } from 'react-icons/bi';
import { useSelector } from 'react-redux';

import ConteneurInformation from '../ConteneurInformation/ConteneurInformation';
import Bouton from '../UI/Bouton/Bouton';
import TableauTasks from './TableauTasks/TableauTasks';
import TableauFunctions from './TableauFunctions/TableauFunctions';
import TaskBuilder from './TaskBuilder/TaskBuilder';
import FunctionBuilder from './FunctionBuilder/FunctionBuilder';
import TaskProfil from './TaskProfil/TaskProfil';
import FunctionViewer from './FunctionProfil/FunctionViewer';

import * as indexConteneurInfo from '../../ressources/glossaires/glossaire_conteneur_info';
import * as type_btn from '../../ressources/glossaires/glossaire_type_de_bouton';
import {
  createTask,
  updateTask,
  deleteTask,
  getTasksByIdProject,
  getFunctions,
  createFunction,
  updateFunction,
  deleteFunction,
  triggerTask,
} from '../../services/api';
import S from './Tasks.module.scss';

/**
 * Composant principal de gestion des tâches planifiées
 * Gère l'affichage, la création, la modification et la suppression des tâches
 */
const Tasks = () => {
  // State management
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' or 'functions'
  const [selectedTask, setSelectedTask] = useState({});
  const [selectedFunction, setSelectedFunction] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const [containerIndex, setContainerIndex] = useState(null);
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [functions, setFunctions] = useState([]);

  const filtre_projects_list_id = useSelector(
    (state) => state.authentifie.filtre_projects_list_id
  );

  // Remap task keys for TaskProfil
  const mapTaskForProfil = useCallback((task) => {
    const mode =
      task.type === 'scheduled' || task.type === 'planned'
        ? 'scheduled'
        : task.mode || 'manual';

    const interval_years =
      task.interval_years ||
      (task.cron_year && task.cron_year !== '*'
        ? parseInt(task.cron_year, 10) || 0
        : 0);
    const interval_months =
      task.interval_months ||
      (task.cron_month && task.cron_month !== '*'
        ? parseInt(task.cron_month, 10) || 0
        : 0);
    const interval_weeks =
      task.interval_weeks ||
      (task.cron_week && task.cron_week !== '*'
        ? parseInt(task.cron_week, 10) || 0
        : 0);
    const interval_days =
      task.interval_days ||
      (task.cron_day && task.cron_day !== '*'
        ? parseInt(task.cron_day, 10) || 0
        : 0);
    const interval_hours =
      task.interval_hours ||
      (task.cron_hour && task.cron_hour !== '*'
        ? parseInt(task.cron_hour, 10) || 0
        : 0);
    const interval_minutes =
      task.interval_minutes ||
      (task.cron_minute && task.cron_minute !== '*'
        ? parseInt(task.cron_minute, 10) || 0
        : 0);
    const interval_seconds =
      task.interval_seconds ||
      (task.cron_second && task.cron_second !== '*'
        ? parseInt(task.cron_second, 10) || 0
        : 0);

    const hasDaysOfWeek =
      task.cron_day_of_week &&
      task.cron_day_of_week !== '*' &&
      task.cron_day_of_week !== '';
    const isPlanned = task.type === 'planned';
    const isSingleExecution =
      task.cron_year !== '*' &&
      task.cron_month !== '*' &&
      task.cron_day !== '*';

    let frequence = '';
    if (mode === 'scheduled') {
      if (hasDaysOfWeek) {
        frequence = 'days_of_week';
      } else if (isPlanned || isSingleExecution) {
        frequence = 'once';
      } else if (
        interval_years > 0 ||
        interval_months > 0 ||
        interval_weeks > 0 ||
        interval_days > 0 ||
        interval_hours > 0 ||
        interval_minutes > 0 ||
        interval_seconds > 0
      ) {
        frequence = 'custom';
      } else if (task.frequence) {
        frequence = task.frequence;
      }
    }

    const hasCronTime =
      task.cron_hour !== undefined &&
      task.cron_hour !== '*' &&
      task.cron_minute !== undefined &&
      task.cron_minute !== '*';
    const timeValue = hasCronTime
      ? `${task.cron_hour}:${`${task.cron_minute}`.padStart(2, '0')}`
      : '';

    return {
      ...task,
      id: task.task_id,
      nom: task.name,
      description: task.description,
      active: task.state === 'Active' || task.state === 'ON' || task.active,
      mode,
      fonctions: task.fonctions || [],
      function_id: task.function_id,
      function_name: task.function_name,
      id_arguments: task.id_arguments || [],
      frequence,
      frequence_date: task.frequence_date || task.cron_start_day || '',
      frequence_time: task.frequence_time || timeValue,
      end_date: task.end_date || task.cron_end_date || '',
      end_time: task.end_time || timeValue,
      interval_years,
      interval_months,
      interval_weeks,
      interval_days,
      interval_hours,
      interval_minutes,
      interval_seconds,
      days_of_week:
        task.days_of_week ||
        (task.cron_day_of_week && task.cron_day_of_week !== '*'
          ? task.cron_day_of_week
          : ''),
      group_taches: task.group_taches || '',
    };
  }, []);

  // Load tasks on component mount and when project filter changes
  const loadTasks = useCallback(async () => {
    try {
      const response = await getTasksByIdProject({
        projectIds: filtre_projects_list_id,
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    }
  }, [filtre_projects_list_id]);

  // Load functions
  const loadFunctions = useCallback(async () => {
    try {
      const response = await getFunctions();
      const responseData = response.data;
      setFunctions(responseData.data || []);
    } catch (error) {
      console.error('Error loading functions:', error);
      setFunctions([]);
    }
  }, []);

  useEffect(() => {
    loadTasks();
    loadFunctions();
  }, [loadTasks, loadFunctions]);

  // Event handlers
  const handleTaskSelection = useCallback(
    (taskInfo) => {
      setSelectedTask(mapTaskForProfil(taskInfo));
      setContainerIndex(indexConteneurInfo.profil);
      setShowInfo(true);
      setIsEditMode(false);
    },
    [mapTaskForProfil]
  );

  const handleEditTask = useCallback(
    (taskInfo) => {
      setSelectedTask(mapTaskForProfil(taskInfo));
      setContainerIndex(indexConteneurInfo.ajouterForm);
      setShowInfo(true);
      setIsEditMode(true);
    },
    [mapTaskForProfil]
  );

  const toggleInfoCreator = useCallback(() => {
    setShowInfo((prev) => {
      if (prev) {
        setShowFullInfo(false);
        return false;
      }
      return true;
    });
  }, []);

  const toggleFullInfo = useCallback(() => {
    setShowFullInfo((prev) => !prev);
  }, []);

  const handleCancel = useCallback(() => {
    setShowInfo(false);
    setContainerIndex(null);
    setIsEditMode(false);
  }, []);

  const createNewTaskEntity = useCallback(() => {
    setShowInfo(true);
    setContainerIndex(indexConteneurInfo.ajouterForm);
    setSelectedTask({});
    setIsEditMode(false);
  }, []);

  const handleAddFunctionInTask = useCallback((task) => {
    setSelectedTask(task);
    setIsEditMode(true);
    setContainerIndex(indexConteneurInfo.ajouterForm);
    setShowInfo(true);
  }, []);

  // Function handlers
  const handleFunctionSelection = useCallback((funcInfo) => {
    setSelectedFunction(funcInfo);
    setContainerIndex(indexConteneurInfo.profilFunction);
    setShowInfo(true);
    setIsEditMode(false);
  }, []);

  const handleEditFunction = useCallback((funcInfo) => {
    setSelectedFunction(funcInfo);
    setContainerIndex(indexConteneurInfo.ajouterForm);
    setShowInfo(true);
    setIsEditMode(true);
  }, []);

  const createNewFunction = useCallback(() => {
    setShowInfo(true);
    setContainerIndex(indexConteneurInfo.ajouterForm);
    setSelectedFunction({});
    setIsEditMode(false);
  }, []);

  // Memoized save handler for tasks
  const handleSaveTask = useCallback(
    async (taskData, isEdit) => {
      try {
        const payload = {
          project_id: taskData.project_id,
          tasks: [taskData],
        };
        if (isEdit) {
          await updateTask({
            taskId: taskData.task_id,
            taskData: payload,
          });
        } else {
          await createTask({ taskData: payload });
        }

        // Recharger la liste des tâches
        await loadTasks();

        setShowInfo(false);
        setContainerIndex(null);
        setIsEditMode(false);
      } catch (err) {
        console.error('Task save error:', err);
        alert(
          'Erreur lors de la sauvegarde de la tâche: ' +
          (err.message || 'Erreur inconnue')
        );
      }
    },
    [loadTasks]
  );

  // Supprimer une tâche puis rafraîchir la liste
  const handleDeleteTask = useCallback(
    async (taskInfo) => {
      const taskId = taskInfo?.task_id || taskInfo?.id;
      if (!taskId) return;

      // Confirmation avant suppression
      if (
        !window.confirm(
          `Êtes-vous sûr de vouloir supprimer la tâche "${taskInfo?.nom || taskInfo?.name || ''
          }" ?`
        )
      ) {
        return;
      }

      try {
        await deleteTask({ taskId });
        await loadTasks();

        // Réinitialiser l'affichage
        setSelectedTask({});
        setShowInfo(false);
        setContainerIndex(null);
        setIsEditMode(false);
      } catch (err) {
        console.error('Task delete error:', err);
        alert(
          'Erreur lors de la suppression de la tâche: ' +
          (err?.message || 'Erreur inconnue')
        );
      }
    },
    [deleteTask, loadTasks]
  );

  // Memoized save handler for functions
  const handleSaveFunction = useCallback(
    async (functionData, isEdit) => {
      try {
        // Get the function ID from selectedFunction state when editing
        const functionId = isEdit ? selectedFunction.id_function : null;

        if (isEdit && functionId) {
          await updateFunction({
            functionId: functionId,
            functionData,
          });
        } else {
          await createFunction({ functionData });
        }

        // Recharger la liste des fonctions
        await loadFunctions();

        setShowInfo(false);
        setContainerIndex(null);
        setIsEditMode(false);
      } catch (err) {
        console.error('Function save error:', err);
        alert(
          'Erreur lors de la sauvegarde de la fonction: ' +
          (err.message || 'Erreur inconnue')
        );
      }
    },
    [loadFunctions, selectedFunction]
  );

  // Supprimer une fonction puis rafraîchir la liste
  const handleDeleteFunction = useCallback(
    async (functionData) => {
      if (!functionData?.id_function) return;

      // Confirmation dialog
      if (
        !window.confirm('Êtes-vous sûr de vouloir supprimer cette fonction ?')
      ) {
        return;
      }

      try {
        await deleteFunction({ functionId: functionData.id_function });
        await loadFunctions();

        // Réinitialiser l'affichage
        setSelectedFunction({});
        setShowInfo(false);
        setContainerIndex(null);
        setIsEditMode(false);
      } catch (err) {
        console.error('Function delete error:', err);
        alert(
          'Erreur lors de la suppression de la fonction: ' +
          (err?.message || 'Erreur inconnue')
        );
      }
    },
    [loadFunctions, deleteFunction]
  );

  // Memoized style classes
  const containerClasses = useMemo(() => {
    const leftContainerStyle = [S.ConteneurCentralGauche];
    const rightContainerStyle = [S.ConteneurCentralDroite];

    if (showInfo && showFullInfo) {
      rightContainerStyle.push(S.ConteneurCentralDroiteFull);
      leftContainerStyle.push(S.ConteneurCentraGaucheVoid);
    } else if (showInfo && !showFullInfo) {
      rightContainerStyle.push(S.ConteneurCentralDroiteOuvert);
    } else {
      rightContainerStyle.push(S.ConteneurCentralDroiteFermer);
      leftContainerStyle.push(S.ConteneurCentraGaucheFermer);
    }

    return {
      left: leftContainerStyle.join(' '),
      right: rightContainerStyle.join(' '),
      toggler: [S.CommutateurInfo],
    };
  }, [showInfo, showFullInfo]);

  return (
    <div className={S.ConteneurTasks}>
      <div className={S.tabsNavigation}>
        <div className={S.tabsContainer}>
          <button
            className={`${S.tab} ${activeTab === 'tasks' ? S.tabActive : ''}`}
            onClick={() => {
              setActiveTab('tasks');
              setShowInfo(false);
              setContainerIndex(null);
              setSelectedTask({});
              setSelectedFunction({});
              setIsEditMode(false);
            }}
          >
            <BiTask size={18} />
            Tâches
          </button>
          <button
            className={`${S.tab} ${activeTab === 'functions' ? S.tabActive : ''
              }`}
            onClick={() => {
              setActiveTab('functions');
              setShowInfo(false);
              setContainerIndex(null);
              setSelectedTask({});
              setSelectedFunction({});
              setIsEditMode(false);
            }}
          >
            <BiCodeAlt size={18} />
            Fonctions
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div className={containerClasses.left}>
          {activeTab === 'tasks' && (
            <TableauTasks
              tasks={tasks}
              taskSelectionnee={handleTaskSelection}
              editerTache={handleEditTask}
              ajouterFonctionDansTache={handleAddFunctionInTask}
              creerTache={createNewTaskEntity}
              supprimerTache={handleDeleteTask}
              executerTache={triggerTask}
            />
          )}
          {activeTab === 'functions' && (
            <TableauFunctions
              functions={functions}
              functionSelectionnee={handleFunctionSelection}
              editerFunction={handleEditFunction}
              creerFunction={createNewFunction}
              supprimerFunction={handleDeleteFunction}
            />
          )}
        </div>
        <div className={containerClasses.right}>
          <Bouton
            ajoutClass={[containerClasses.toggler, S.expandTO].join(' ')}
            cliquer={toggleFullInfo}
            rond={true}
            type={type_btn.danger}
          >
            <BiArrowFromLeft size={15} />
          </Bouton>
          <Bouton
            ajoutClass={S.expand}
            cliquer={toggleInfoCreator}
            rond={true}
            type={type_btn.danger}
          >
            <BiArrowFromLeft size={15} />
          </Bouton>
          <ConteneurInformation
            selectionInfo={containerIndex}
            ajouterForm={
              activeTab === 'tasks' ? (
                <TaskBuilder
                  initialTask={selectedTask}
                  isEditMode={isEditMode}
                  annuler={handleCancel}
                  sauvegarderTache={handleSaveTask}
                />
              ) : (
                <FunctionBuilder
                  initialFunction={selectedFunction}
                  isEditMode={isEditMode}
                  annuler={handleCancel}
                  sauvegarderFunction={handleSaveFunction}
                />
              )
            }
            profil={
              <TaskProfil
                task={selectedTask}
                onEdit={() => handleEditTask(selectedTask)}
                onDelete={() => handleDeleteTask(selectedTask)}
                onAddFunction={() => handleAddFunctionInTask(selectedTask)}
              />
            }
            profilFunction={
              <FunctionViewer
                functionData={selectedFunction}
                editerFunction={handleEditFunction}
                onDelete={() => handleDeleteFunction(selectedFunction)}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Tasks;
